import React, { useState } from 'react';
import axios from 'axios';
import Header from './components/Header';
import OptimizationForm from './components/OptimizationForm';
import PresetScenarios from './components/PresetScenarios';
import ErrorDisplay from './components/ErrorDisplay';
import ResultsDisplay from './components/ResultsDisplay';
import ChartsDisplay from './components/ChartsDisplay';
import CircularProgress from '@mui/material/CircularProgress';

// Use environment variables for backend URL
const backendURL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

function App() {
  const [expectedReturns, setExpectedReturns] = useState('[0.1, 0.12, 0.14]');
  const [covMatrix, setCovMatrix] = useState('[[0.005, -0.002, 0.004], [-0.002, 0.004, -0.002], [0.004, -0.002, 0.006]]');
  const [riskFreeRate, setRiskFreeRate] = useState('2');
  const [targetReturn, setTargetReturn] = useState('');
  const [result, setResult] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [error, setError] = useState(null);
  const [optimalWeightsChartData, setOptimalWeightsChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1'];

  const handleOptimize = async (e) => {
    e.preventDefault();

    try {
      const parsedExpectedReturns = JSON.parse(expectedReturns);
      const parsedCovMatrix = JSON.parse(covMatrix);

      if (parsedExpectedReturns.length !== parsedCovMatrix.length) {
        console.log("Setting error: Expected returns and covariance matrix must have the same number of assets.");
        setError("Expected returns and covariance matrix must have the same number of assets.");
        return;
      }

      if (!parsedCovMatrix.every((row) => row.length === parsedCovMatrix.length)) {
        console.log("Setting error: Covariance matrix must be a square matrix.");
        setError("Covariance matrix must be a square matrix.");
        return;
      }

      setError(null);
      setLoading(true);

      const response = await axios.post(`${backendURL}/optimize`, {
        expected_returns: parsedExpectedReturns,
        cov_matrix: parsedCovMatrix,
        risk_free_rate: riskFreeRate ? parseFloat(riskFreeRate) : 0,
        target_return: targetReturn ? parseFloat(targetReturn) : null,
      });

      // Transform Optimal Weights Data for Charting
      const transformedOptimalWeights = response.data.optimal_weights.map((weight, index) => ({
        name: `Asset ${index + 1}`,
        value: weight,
      }));
      setOptimalWeightsChartData(transformedOptimalWeights);

      setResult(response.data.optimal_weights);
      setPerformance(response.data.performance);
    } catch (error) {
      if (error instanceof SyntaxError) {
        setError("Please enter valid JSON for both expected returns and covariance matrix.");
      } else {
        console.error("Error optimizing portfolio:", error);
        setError("An error occurred while optimizing the portfolio. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGAOptimize = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading to true to indicate that processing is underway

    try {
      const parsedExpectedReturns = JSON.parse(expectedReturns);
      const parsedCovMatrix = JSON.parse(covMatrix);

      if (parsedExpectedReturns.length !== parsedCovMatrix.length) {
        console.log("Setting error: Expected returns and covariance matrix must have the same number of assets.");
        setError("Expected returns and covariance matrix must have the same number of assets.");
        setLoading(false);
        return;
      }

      if (!parsedCovMatrix.every((row) => row.length === parsedCovMatrix.length)) {
        console.log("Setting error: Covariance matrix must be a square matrix.");
        setError("Covariance matrix must be a square matrix.");
        setLoading(false);
        return;
      }

      setError(null);

      const response = await axios.post(`${backendURL}/optimize_ga`, {
        expected_returns: parsedExpectedReturns,
        cov_matrix: parsedCovMatrix,
        risk_free_rate: riskFreeRate ? parseFloat(riskFreeRate) : 0,
      });

      // Transform Optimal Weights Data for Charting
      const transformedOptimalWeights = response.data.optimal_weights.map((weight, index) => ({
        name: `Asset ${index + 1}`,
        value: weight,
      }));
      setOptimalWeightsChartData(transformedOptimalWeights);

      setResult(response.data.optimal_weights);
      setPerformance(response.data.performance);
    } catch (error) {
      if (error instanceof SyntaxError) {
        setError("Please enter valid JSON for both expected returns and covariance matrix.");
      } else {
        console.error("Error optimizing portfolio:", error);
        setError("An error occurred while optimizing the portfolio using Genetic Algorithm. Please try again.");
      }
    } finally {
      setLoading(false); // Set loading to false after the process finishes
    }
  };

  const presetScenarios = [
    {
      label: 'Scenario 1',
      expectedReturns: '[0.1, 0.11, 0.12, 0.09]',
      covMatrix: '[[0.004, 0.001, 0.002, 0.001], [0.001, 0.005, 0.002, 0.003], [0.002, 0.002, 0.006, 0.002], [0.001, 0.003, 0.002, 0.003]]',
      riskFreeRate: '2',
      targetReturn: '',
      tooltip: 'Higher average expected returns with moderate correlations.'
    },
    {
      label: 'Scenario 2',
      expectedReturns: '[0.15, 0.08, 0.1, 0.07]',
      covMatrix: '[[0.003, 0.001, 0.0015, 0.001], [0.001, 0.004, 0.002, 0.0015], [0.0015, 0.002, 0.005, 0.002], [0.001, 0.0015, 0.002, 0.0035]]',
      riskFreeRate: '1.5',
      targetReturn: '',
      tooltip: 'High variance assets with varying degrees of correlation.'
    },
    {
      label: 'Scenario 3',
      expectedReturns: '[0.12, 0.14, 0.13]',
      covMatrix: '[[0.006, 0.002, 0.003], [0.002, 0.005, 0.002], [0.003, 0.002, 0.004]]',
      riskFreeRate: '3',
      targetReturn: '',
      tooltip: 'Small asset pool with a relatively high risk-free rate.'
    },
    {
      label: 'Scenario 4',
      expectedReturns: '[0.09, 0.11, 0.1, 0.08]',
      covMatrix: '[[0.0045, 0.001, 0.001, 0.0005], [0.001, 0.0035, 0.0015, 0.001], [0.001, 0.0015, 0.004, 0.0015], [0.0005, 0.001, 0.0015, 0.0025]]',
      riskFreeRate: '1.8',
      targetReturn: '',
      tooltip: 'Moderate return expectations with low correlations.'
    },
    {
      label: 'Scenario 5',
      expectedReturns: '[0.08, 0.09, 0.1, 0.07, 0.11]',
      covMatrix: '[[0.005, 0.001, 0.0015, 0.001, 0.002], [0.001, 0.004, 0.001, 0.001, 0.0015], [0.0015, 0.001, 0.0055, 0.002, 0.001], [0.001, 0.001, 0.002, 0.003, 0.0015], [0.002, 0.0015, 0.001, 0.0015, 0.004]]',
      riskFreeRate: '2.5',
      targetReturn: '',
      tooltip: 'Diverse asset pool with varied covariances.'
    },
    {
      label: 'Scenario 6',
      expectedReturns: '[0.1, 0.13, 0.12, 0.09]',
      covMatrix: '[[0.004, 0.002, 0.0015, 0.001], [0.002, 0.006, 0.002, 0.0015], [0.0015, 0.002, 0.005, 0.002], [0.001, 0.0015, 0.002, 0.0035]]',
      riskFreeRate: '1.7',
      targetReturn: '',
      tooltip: 'Moderate risk and moderate expected returns with diverse covariance.'
    }
  ];

  const handlePresetScenario = (scenario) => {
    setExpectedReturns(scenario.expectedReturns);
    setCovMatrix(scenario.covMatrix);
    setRiskFreeRate(scenario.riskFreeRate);
    setTargetReturn(scenario.targetReturn);
  };

  return (
    <div className="App">
      <Header />
      <OptimizationForm
        expectedReturns={expectedReturns}
        covMatrix={covMatrix}
        riskFreeRate={riskFreeRate}
        targetReturn={targetReturn}
        setExpectedReturns={setExpectedReturns}
        setCovMatrix={setCovMatrix}
        setRiskFreeRate={setRiskFreeRate}
        setTargetReturn={setTargetReturn}
        handleOptimize={handleOptimize}
        handleGAOptimize={handleGAOptimize}
        loading={loading}
      />
      <PresetScenarios scenarios={presetScenarios} handlePresetScenario={handlePresetScenario} />
      {loading && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CircularProgress />
          <div>Optimizing... Please wait</div>
        </div>
      )}
      <ErrorDisplay error={error} />
      <ChartsDisplay optimalWeightsChartData={optimalWeightsChartData} performance={performance} COLORS={COLORS} />
      <ResultsDisplay result={result} />
    </div>
  );
}

export default App;
